import React, { useState, useEffect, useContext, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import moment from "moment";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import ButtonBase from "@material-ui/core/ButtonBase";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";
import LogoLoader from "../LogoLoader";
import { FormDialogHeader, FormSection, useFormDialogStyles } from "../FormDialog";

// ---------------------------------------------------------------------------
// Horário de trabalho (formato salvo no banco: um item por dia, horário vazio
// = não atende nesse dia)
// ---------------------------------------------------------------------------
const WEEKDAYS = [
  { weekday: "Segunda-feira", weekdayEn: "monday", short: "Seg" },
  { weekday: "Terça-feira", weekdayEn: "tuesday", short: "Ter" },
  { weekday: "Quarta-feira", weekdayEn: "wednesday", short: "Qua" },
  { weekday: "Quinta-feira", weekdayEn: "thursday", short: "Qui" },
  { weekday: "Sexta-feira", weekdayEn: "friday", short: "Sex" },
  { weekday: "Sábado", weekdayEn: "saturday", short: "Sáb" },
  { weekday: "Domingo", weekdayEn: "sunday", short: "Dom" },
];
const WEEKDAYS_ONLY = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const DEFAULT_START = "08:00";
const DEFAULT_END = "18:00";

const emptySchedule = () =>
  WEEKDAYS.map(({ weekday, weekdayEn }) => ({ weekday, weekdayEn, startTime: "", endTime: "" }));

// Aceita o que vier do banco (ordem ou dias faltando) e devolve os 7 dias.
const normalizeSchedule = (saved) => {
  const list = Array.isArray(saved) ? saved : [];
  return WEEKDAYS.map(({ weekday, weekdayEn }) => {
    const found = list.find((d) => d && d.weekdayEn === weekdayEn) || {};
    return {
      weekday,
      weekdayEn,
      startTime: found.startTime || "",
      endTime: found.endTime || "",
    };
  });
};

const isWorkingDay = (d) => Boolean(d.startTime && d.endTime);

// "08:00" < "18:00" — comparação de texto funciona para HH:mm.
const isInvalidRange = (d) => isWorkingDay(d) && d.startTime >= d.endTime;

const getInitials = (name) =>
  name
    ? name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()
    : "?";

// ---------------------------------------------------------------------------
// Estilos
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  roles: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.25),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  role: {
    display: "block",
    textAlign: "left",
    width: "100%",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1.5, 1.75),
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
  },
  roleActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  roleTitle: { fontWeight: 600, fontSize: 14 },
  roleText: { display: "block", marginTop: 4, fontSize: 12.5, color: theme.palette.text.secondary, lineHeight: 1.45 },
  queues: { display: "flex", flexWrap: "wrap", gap: 6 },
  queueChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    padding: "5px 11px",
    color: theme.palette.text.primary,
  },
  queueChipActive: {
    borderColor: "transparent",
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  queueDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  linkButton: { textTransform: "none", fontWeight: 600, padding: "2px 6px", minWidth: 0 },
  warning: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.45,
    backgroundColor: alpha(theme.palette.warning.main, theme.palette.type === "dark" ? 0.16 : 0.12),
    "& svg": { color: theme.palette.warning.main, fontSize: 18, marginTop: 1 },
  },
  switchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  switchLabel: { fontWeight: 500 },
  days: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    overflow: "hidden",
  },
  day: {
    display: "grid",
    gridTemplateColumns: "44px 1fr auto",
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "34px 1fr auto", gap: 6, paddingLeft: 10 },
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1, 0.75, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  dayName: { fontWeight: 600, fontSize: 13 },
  dayTimes: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: { gap: 4, flexWrap: "nowrap" },
  },
  timeField: {
    width: 120,
    "& input": { padding: "8px 10px", fontVariantNumeric: "tabular-nums" },
    [theme.breakpoints.down("xs")]: {
      width: "auto",
      flex: "1 1 0",
      minWidth: 0,
      "& input": { padding: "6px 8px", fontSize: 13 },
      // no celular o toque já abre o seletor de hora; o ícone só ocupa espaço
      "& input::-webkit-calendar-picker-indicator": { display: "none" },
    },
  },
  dayOff: { fontSize: 13, color: theme.palette.text.secondary },
  dayError: { fontSize: 12, color: theme.palette.error.main },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontWeight: 700,
    fontSize: 15,
    color: "#fff",
    backgroundColor: theme.palette.primary.main,
    flexShrink: 0,
  },
  headerRow: { display: "flex", alignItems: "center" },
}));

// ---------------------------------------------------------------------------
// Validação
// ---------------------------------------------------------------------------
const buildSchema = (isEdit) =>
  Yup.object().shape({
    name: Yup.string()
      .trim()
      .min(3, "Nome deve ter pelo menos 3 caracteres")
      .max(256, "Nome não pode ultrapassar 256 caracteres")
      .required("Informe o nome"),
    email: Yup.string()
      .trim()
      .email("E-mail inválido")
      .max(128, "E-mail não pode ultrapassar 128 caracteres")
      .required("Informe o e-mail"),
    password: isEdit
      ? Yup.string()
          .min(6, "A senha precisa ter pelo menos 6 caracteres")
          .max(256, "Senha não pode ultrapassar 256 caracteres")
      : Yup.string()
          .min(6, "A senha precisa ter pelo menos 6 caracteres")
          .max(256, "Senha não pode ultrapassar 256 caracteres")
          .required("Informe uma senha"),
  });

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
// focusPassword: abre com o cursor no campo de senha ("Trocar senha").
const UserModal = ({ open, onClose, userId, focusPassword }) => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();
  const initialState = { name: "", email: "", password: "", currentPassword: "", profile: "user" };

  const { user: loggedInUser } = useContext(AuthContext);
  const isAdmin = loggedInUser?.profile === "admin";
  const isEdit = Boolean(userId);
  const isSelf = isEdit && Number(loggedInUser?.id) === Number(userId);

  // No próprio cadastro, trocar e-mail ou senha pede a senha atual (o
  // backend confere). Admin editando outra pessoa não precisa.
  const needsCurrentPassword = (values) =>
    isSelf &&
    (Boolean(values.password) ||
      (values.email || "").trim().toLowerCase() !== (user.email || "").trim().toLowerCase());

  const [user, setUser] = useState(initialState);
  const [createdAt, setCreatedAt] = useState(null);
  const [profile, setProfile] = useState("user");
  const [queues, setQueues] = useState([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isServiceProvider, setIsServiceProvider] = useState(false);
  const [workSchedule, setWorkSchedule] = useState(emptySchedule);
  const [loadingData, setLoadingData] = useState(false);
  const passwordRef = useRef(null);

  const { whatsApps } = useWhatsApps();

  // Filas da empresa (só quem pode editar filas precisa da lista).
  useEffect(() => {
    if (!open || !isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/queue");
        const list = data || [];
        setQueues(list);
        // Usuário novo já nasce em todas as filas: sem fila ele não enxerga
        // nenhum atendimento, e esse vínculo esquecido travava a configuração.
        if (!userId) setSelectedQueueIds(list.map((q) => q.id));
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open, userId, isAdmin]);

  useEffect(() => {
    if (!open || !userId) return;
    (async () => {
      setLoadingData(true);
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser({ ...initialState, name: data.name || "", email: data.email || "", profile: data.profile || "user" });
        setProfile(data.profile || "user");
        setCreatedAt(data.createdAt || null);
        setSelectedQueueIds((data.queues || []).map((q) => q.id));
        setWhatsappId(data.whatsappId ? data.whatsappId : "");
        setIsServiceProvider(Boolean(data.isServiceProvider));
        setWorkSchedule(normalizeSchedule(data.workSchedule));
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, open]);

  const resetState = () => {
    setUser(initialState);
    setProfile("user");
    setCreatedAt(null);
    setSelectedQueueIds([]);
    setWhatsappId("");
    setShowPassword(false);
    setIsServiceProvider(false);
    setWorkSchedule(emptySchedule());
  };

  const handleClose = () => {
    onClose();
    resetState();
  };

  // ── Agenda ────────────────────────────────────────────────────────────────
  const updateDay = (index, patch) =>
    setWorkSchedule((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  const toggleDay = (index) => {
    const day = workSchedule[index];
    if (isWorkingDay(day)) {
      updateDay(index, { startTime: "", endTime: "" });
      return;
    }
    // Ao ligar um dia, usa o horário do primeiro dia já configurado.
    const ref = workSchedule.find(isWorkingDay);
    updateDay(index, {
      startTime: ref ? ref.startTime : DEFAULT_START,
      endTime: ref ? ref.endTime : DEFAULT_END,
    });
  };

  const copyMondayToWeekdays = () => {
    const monday = workSchedule[0];
    setWorkSchedule((prev) =>
      prev.map((d) =>
        WEEKDAYS_ONLY.includes(d.weekdayEn)
          ? { ...d, startTime: monday.startTime, endTime: monday.endTime }
          : d
      )
    );
  };

  const handleToggleServiceProvider = (checked) => {
    setIsServiceProvider(checked);
    // Primeira vez: já sugere seg a sex, 08:00 às 18:00.
    if (checked && !workSchedule.some(isWorkingDay)) {
      setWorkSchedule((prev) =>
        prev.map((d) =>
          WEEKDAYS_ONLY.includes(d.weekdayEn)
            ? { ...d, startTime: DEFAULT_START, endTime: DEFAULT_END }
            : d
        )
      );
    }
  };

  const scheduleHasError = isServiceProvider && workSchedule.some(isInvalidRange);

  // ── Salvar ────────────────────────────────────────────────────────────────
  const handleSaveUser = async (values, setFieldError) => {
    const userData = {
      name: values.name.trim(),
      email: values.email.trim(),
      password: values.password,
    };
    if (needsCurrentPassword(values)) {
      userData.currentPassword = values.currentPassword;
    }
    if (isAdmin) {
      Object.assign(userData, {
        profile,
        whatsappId: whatsappId || null,
        queueIds: selectedQueueIds,
        isServiceProvider,
        workSchedule: isServiceProvider ? workSchedule : null,
      });
    }

    try {
      if (userId) {
        await api.put(`/users/${userId}`, userData);
      } else {
        await api.post("/users", userData);
      }
      toast.success(i18n.t("userModal.success"));
      handleClose();
    } catch (error) {
      // O modal continua aberto para o usuário corrigir (ex.: e-mail já usado).
      const message = error?.response?.data?.error;
      if (setFieldError && /senha atual/i.test(message || "")) {
        setFieldError("currentPassword", message);
        return;
      }
      toastError(error);
    }
  };

  const title = isEdit
    ? loggedInUser?.id === userId
      ? "Meu perfil"
      : "Editar usuário"
    : "Novo usuário";
  const subtitle = isEdit
    ? [user.name, createdAt && `criado em ${moment(createdAt).format("DD/MM/YYYY")}`]
        .filter(Boolean)
        .join(" · ")
    : "Ele vai entrar no sistema com o e-mail e a senha abaixo.";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      classes={{ paper: dialogClasses.paper }}
      aria-labelledby="user-dialog-title"
      TransitionProps={{
        onEntered: () => {
          if (focusPassword && passwordRef.current) passwordRef.current.focus();
        },
      }}
    >
      {loadingData && <LogoLoader overlay />}
      <Formik
        initialValues={user}
        enableReinitialize
        validationSchema={buildSchema(isEdit)}
        onSubmit={async (values, { setFieldError, setFieldTouched }) => {
          if (needsCurrentPassword(values) && !values.currentPassword) {
            setFieldTouched("currentPassword", true, false);
            setFieldError("currentPassword", "Informe a sua senha atual para trocar o e-mail ou a senha.");
            return;
          }
          if (scheduleHasError) return;
          // Aguarda a requisição: enquanto isso o Formik mantém isSubmitting
          // e o botão desabilitado, evitando envio duplicado.
          await handleSaveUser(values, setFieldError);
        }}
      >
        {({ touched, errors, isSubmitting, values }) => (
          <Form style={{ display: "contents" }}>
            <div className={classes.headerRow}>
              <div style={{ paddingLeft: 24 }}>
                <div className={classes.avatar} aria-hidden="true">
                  {getInitials(values.name)}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <FormDialogHeader
                  id="user-dialog-title"
                  title={title}
                  subtitle={subtitle}
                  onClose={handleClose}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <DialogContent dividers className={dialogClasses.content}>
              <FormSection title="Dados de acesso">
                <div className={classes.grid2}>
                  <Field
                    as={TextField}
                    label="Nome"
                    autoFocus={!focusPassword}
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  <Field
                    as={TextField}
                    label="E-mail (login)"
                    name="email"
                    type="email"
                    autoComplete="off"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  <Field
                    as={TextField}
                    label={isEdit ? "Nova senha" : "Senha"}
                    placeholder={isEdit ? "Deixe em branco para manter" : ""}
                    InputLabelProps={isEdit ? { shrink: true } : undefined}
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="new-password"
                    inputRef={passwordRef}
                    error={touched.password && Boolean(errors.password)}
                    helperText={(touched.password && errors.password) || "Mínimo de 6 caracteres."}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {needsCurrentPassword(values) && (
                    <Field
                      as={TextField}
                      label="Senha atual"
                      type="password"
                      name="currentPassword"
                      autoComplete="current-password"
                      error={touched.currentPassword && Boolean(errors.currentPassword)}
                      helperText={
                        (touched.currentPassword && errors.currentPassword) ||
                        "Para confirmar que é você."
                      }
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  )}
                  <Can
                    role={loggedInUser.profile}
                    perform="user-modal:editProfile"
                    yes={() => (
                      <TextField
                        select
                        label="Conexão padrão"
                        value={whatsappId}
                        onChange={(e) => setWhatsappId(e.target.value)}
                        helperText="Usada quando ele inicia uma conversa."
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      >
                        <MenuItem value="">
                          <em>Nenhuma</em>
                        </MenuItem>
                        {whatsApps.map((w) => (
                          <MenuItem key={w.id} value={w.id}>
                            {w.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </div>
              </FormSection>

              <Can
                role={loggedInUser.profile}
                perform="user-modal:editProfile"
                yes={() => (
                  <FormSection title="Permissão">
                    <div className={classes.roles} role="radiogroup" aria-label="Permissão">
                      {[
                        {
                          key: "admin",
                          label: "Admin",
                          text: "Gerencia usuários, conexões, filas, financeiro e configurações.",
                        },
                        {
                          key: "user",
                          label: "Atendente",
                          text: "Atende as conversas das filas abaixo e usa a própria agenda.",
                        },
                      ].map((r) => (
                        <ButtonBase
                          key={r.key}
                          role="radio"
                          aria-checked={profile === r.key}
                          className={`${classes.role} ${profile === r.key ? classes.roleActive : ""}`}
                          onClick={() => setProfile(r.key)}
                        >
                          <span className={classes.roleTitle}>{r.label}</span>
                          <span className={classes.roleText}>{r.text}</span>
                        </ButtonBase>
                      ))}
                    </div>
                  </FormSection>
                )}
              />

              <Can
                role={loggedInUser.profile}
                perform="user-modal:editQueues"
                yes={() => (
                  <FormSection
                    title="Filas que atende"
                    action={
                      queues.length > 0 && (
                        <Button
                          size="small"
                          color="primary"
                          className={classes.linkButton}
                          onClick={() =>
                            setSelectedQueueIds(
                              selectedQueueIds.length === queues.length ? [] : queues.map((q) => q.id)
                            )
                          }
                        >
                          {selectedQueueIds.length === queues.length ? "Limpar" : "Marcar todas"}
                        </Button>
                      )
                    }
                  >
                    {queues.length === 0 ? (
                      <Typography variant="body2" className={dialogClasses.muted}>
                        Nenhuma fila cadastrada ainda.
                      </Typography>
                    ) : (
                      <div className={classes.queues} role="group" aria-label="Filas">
                        {queues.map((q) => {
                          const active = selectedQueueIds.includes(q.id);
                          return (
                            <ButtonBase
                              key={q.id}
                              aria-pressed={active}
                              className={`${classes.queueChip} ${active ? classes.queueChipActive : ""}`}
                              onClick={() =>
                                setSelectedQueueIds(
                                  active
                                    ? selectedQueueIds.filter((id) => id !== q.id)
                                    : [...selectedQueueIds, q.id]
                                )
                              }
                            >
                              <span className={classes.queueDot} style={{ backgroundColor: q.color }} />
                              {q.name}
                            </ButtonBase>
                          );
                        })}
                      </div>
                    )}
                    {queues.length > 0 && selectedQueueIds.length === 0 && (
                      <div className={classes.warning}>
                        <WarningRoundedIcon />
                        <span>Sem nenhuma fila, este usuário não recebe atendimentos.</span>
                      </div>
                    )}
                  </FormSection>
                )}
              />

              <Can
                role={loggedInUser.profile}
                perform="user-modal:editProfile"
                yes={() => (
                  <FormSection title="Agenda">
                    <div className={classes.switchRow}>
                      <div>
                        <div className={classes.switchLabel}>Atende na agenda</div>
                        <Typography variant="body2" className={dialogClasses.muted}>
                          Aparece como profissional nos agendamentos e para a IA.
                        </Typography>
                      </div>
                      <Switch
                        checked={isServiceProvider}
                        onChange={(e) => handleToggleServiceProvider(e.target.checked)}
                        color="primary"
                        inputProps={{ "aria-label": "Atende na agenda" }}
                      />
                    </div>

                    {isServiceProvider && (
                      <>
                        <div className={classes.days}>
                          {workSchedule.map((d, index) => {
                            const working = isWorkingDay(d);
                            const label = WEEKDAYS[index].short;
                            return (
                              <div className={classes.day} key={d.weekdayEn}>
                                <span className={classes.dayName} title={d.weekday}>
                                  {label}
                                </span>
                                {working ? (
                                  <div style={{ minWidth: 0 }}>
                                    <div className={classes.dayTimes}>
                                      <TextField
                                        type="time"
                                        variant="outlined"
                                        size="small"
                                        className={classes.timeField}
                                        value={d.startTime}
                                        onChange={(e) => updateDay(index, { startTime: e.target.value })}
                                        inputProps={{ "aria-label": `Início ${d.weekday}`, step: 300 }}
                                      />
                                      <span className={dialogClasses.muted}>até</span>
                                      <TextField
                                        type="time"
                                        variant="outlined"
                                        size="small"
                                        className={classes.timeField}
                                        value={d.endTime}
                                        onChange={(e) => updateDay(index, { endTime: e.target.value })}
                                        inputProps={{ "aria-label": `Fim ${d.weekday}`, step: 300 }}
                                      />
                                    </div>
                                    {isInvalidRange(d) && (
                                      <div className={classes.dayError}>
                                        O fim precisa ser depois do início.
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className={classes.dayOff}>Não atende</span>
                                )}
                                <Switch
                                  size="small"
                                  color="primary"
                                  checked={working}
                                  onChange={() => toggleDay(index)}
                                  inputProps={{ "aria-label": `Atende ${d.weekday}` }}
                                />
                              </div>
                            );
                          })}
                        </div>
                        {isWorkingDay(workSchedule[0]) && (
                          <div>
                            <Button
                              size="small"
                              color="primary"
                              className={classes.linkButton}
                              onClick={copyMondayToWeekdays}
                            >
                              Copiar horário de segunda para os dias úteis
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </FormSection>
                )}
              />
            </DialogContent>

            <DialogActions className={dialogClasses.actions}>
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
                className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}
              >
                Cancelar
              </Button>
              <div className={dialogClasses.progressWrapper}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disableElevation
                  disabled={isSubmitting || scheduleHasError}
                  className={dialogClasses.actionButton}
                >
                  {isEdit ? "Salvar alterações" : "Criar usuário"}
                </Button>
                {isSubmitting && <CircularProgress size={24} className={dialogClasses.buttonProgress} />}
              </div>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default UserModal;
